import { QueryClient, type QueryKey, type UseQueryResult, useQuery } from "@tanstack/react-query"
import type { AxiosError } from "axios"

import { productTypeAdapter } from "@/3514/adapters"
import { EProductTypeQueryKey } from "@/3514/config"
import { productTypeFetcher } from "@/3514/fetchers"
// The declaring module, not the "@/3514/hooks" barrel. use-user reaches this file through
// queries/index, so going back up through the barrel makes hooks/index re-enter itself while it is
// still evaluating - and whatever it had not yet re-exported reads as undefined further down
// (`TypeError: (0, utils_1.getCssIdentifierFromFileName) is not a function`).
import { type TUserState, useUser } from "@/3514/hooks/use-user"
import type { IProductTypeDto, IProductTypeModel } from "@/3514/types"
import { hours, seconds } from "@/3514/utils/time.utils"

type TProductTypeQuery = UseQueryResult<IProductTypeModel, AxiosError>

function useProductTypeQuery(): ReturnType<() => TProductTypeQuery> {
    const { isLoaded: isUserLoaded }: TUserState = useUser()

    return useQuery<IProductTypeDto, AxiosError, IProductTypeModel, QueryKey>({
        queryKey: EProductTypeQueryKey(),
        queryFn: productTypeFetcher,
        select: productTypeAdapter,
        refetchInterval: hours(2),
        staleTime: hours(2),
        gcTime: hours(2),
        retry: 1,
        retryDelay: seconds(2),
        enabled: isUserLoaded,
        networkMode: "offlineFirst",
        notifyOnChangeProps: ["data", "isSuccess", "isLoading", "refetch", "isFetching", "isError"],
        experimental_prefetchInRender: true
    })
}

async function productTypeQueryPrefetcher({ qc }: { qc: QueryClient }): Promise<void> {
    await qc.prefetchQuery<IProductTypeDto, AxiosError, IProductTypeModel, QueryKey>({
        queryKey: EProductTypeQueryKey(),
        queryFn: productTypeFetcher,
        staleTime: hours(2),
        gcTime: hours(2),
        retry: false
    })
}

export { useProductTypeQuery, productTypeQueryPrefetcher }
export type { TProductTypeQuery }
